<template>
  <div
    ref="bannerInjection"
    class="banner-injection"
  >
    <div class="listing-banner-wrapper">
      <component
        :is="currentComponent"
        v-if="isShowBanner"
        v-model:factoidIsShown="factoidIsShown"
        v-banner-view-tracker="getBannerEventParams"
        :style="bannerBackground"
        :banner="props.banner.banner_params"
        class="listing-banner"
        :default-sogu-fields="defaultSoguFields"
        @on-click="handleBannerClickEvent"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import BannerDatesSketch from './BannerDatesSketch.vue';
  import BannerDates from './BannerDates.vue';
  import BannerUniversal from './BannerUniversal.vue';
  import BannerUniversalSketch from './BannerUniversalSketch.vue';
  import BannerUniversalTags from './BannerUniversalTags.vue';
  import BannerUniversalSketchTags from './BannerUniversalSketchTags.vue';
  import BannerSubscription from './BannerSubscription.vue';
  import BannerFactoid from './BannerFactoid.vue';
  import type { GetListingBannersItemResponse } from '@/shared/api/experience';
  import { ref, computed } from 'vue';
  import { trackEvent, useViewTracker } from '@/entities/sogu';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';

  interface Props {
    banner: GetListingBannersItemResponse;
    defaultSoguFields: ContextGeoSubject;
    isAuthenticatedUser: boolean;
  }

  const props = defineProps<Props>();

  const factoidIsShown = ref(props.banner.key === 'factoid');

  const isShowBanner = computed(() => !!props.banner);

  const bannerBackground = computed(() => {
    return {
      background: props.banner.banner_params?.background,
    };
  });

  const currentComponent = computed(() => {
    if (!props.banner) {
      return null;
    }

    const bannerTypes: Record<string, Component> = {
      universal: BannerUniversal,
      universal_sketch: BannerUniversalSketch,
      universal_tags: BannerUniversalTags,
      universal_sketch_tags: BannerUniversalSketchTags,
      dates: BannerDates,
      dates_sketch: BannerDatesSketch,
      subscription: BannerSubscription,
      factoid: BannerFactoid,
    };

    if (factoidIsShown.value) {
      return props.isAuthenticatedUser ? bannerTypes.factoid : bannerTypes[props.banner.template];
    }

    return bannerTypes[props.banner.template] || null;
  });

  const bannerInjection = ref<HTMLElement | null>(null);

  const viewEventName = computed(() => {
    return props.banner.template === 'subscription' ? 'listing:factoid_card.show' : 'listing:banner.view';
  });

  const getBannerEventParams = () => ({
    ...props.defaultSoguFields.soguStamp,
    banner_type: props.banner.template,
    banner_name: props.banner.key,
  });
  const vBannerViewTracker = useViewTracker(viewEventName.value);

  const handleBannerClickEvent = () => {
    trackEvent('listing:banner.click', getBannerEventParams());
  };
</script>

<style scoped lang="scss">
  .banner-injection {
    display: flex;
    height: 100%;
  }

  .listing-banner-wrapper {
    display: flex;
    width: 100%;
  }

  .listing-banner {
    box-sizing: border-box;
    width: 100%;
    height: 456px;
    min-height: 456px;
  }
</style>
